import { keyBy, mapValues, omit } from 'es-toolkit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { Document, PdfOrDocument, PdfPage } from '@paragon/contract-library';
import { omitBy } from '@paragon/utilities';

import { IndexMap } from './documentTools';

export interface SanitizedDocument {
  readonly id: number;
  readonly index: number;
  readonly name: string;
  readonly url: string;
  readonly pdfPages: readonly PdfPage[];
  readonly componentDesignId: number;
  readonly notes: string;
  readonly componentRisk: string | undefined;
  readonly riskIncreaseReasons: readonly string[] | undefined;
  readonly riskDecreaseReasons: readonly string[] | undefined;

  readonly notesModified?: boolean;
  readonly designServiceComponentId?: string | undefined;

  // TODO Outdated?
  readonly layer?: { remove(): void };
}

interface DocumentsState {
  readonly activeDocumentId: number | null;
  readonly activePageNumber: number;
  readonly pdfData: { readonly [id: string]: SanitizedDocument };
  readonly pagesFlyout: unknown;
  readonly pdfCount: number;
}

const initialState: DocumentsState = {
  activeDocumentId: null,
  activePageNumber: 0,
  pdfData: {},
  pagesFlyout: { isOpen: false },

  // The number of documents that the currently loaded job should have, which will allow the
  //   document queue to display spinners for loading documents once GET /jobs doesn't eager-load
  //   them
  pdfCount: 0,
};

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    jobReset: () => initialState,
    documentsLoaded: (
      state,
      action: PayloadAction<{ readonly documents: readonly PdfOrDocument[]; readonly newlyCreated?: boolean }>,
    ) => {
      const { documents, newlyCreated } = action.payload;

      const existingDocumentIds = Object.keys(state.pdfData).map((id) => parseInt(id, 10));
      const newDocumentCount = documents.filter((doc) => !existingDocumentIds.includes(doc.id)).length;

      let updatedPdfData = { ...state.pdfData };
      for (const document of documents) {
        const {
          id,
          index,
          name,
          url,
          pdfPages,
          componentDesignId,
          notes,
          componentRisk,
          riskIncreaseReasons,
          riskDecreaseReasons,
        } = document;

        updatedPdfData[id] = {
          id,
          index,
          name,
          url,
          pdfPages: castDraft(pdfPages),
          componentDesignId,
          notes,
          componentRisk,
          riskIncreaseReasons: castDraft(riskIncreaseReasons),
          riskDecreaseReasons: castDraft(riskDecreaseReasons),
        };
      }

      if (newlyCreated) {
        const indexOfFirstAddedDocument = Math.min(...documents.map((pdf) => pdf.index));
        const addedDocumentIds = documents.map((pdf) => pdf.id);

        updatedPdfData = mapValues(updatedPdfData, (existingPdf) => {
          if (addedDocumentIds.includes(existingPdf.id) || existingPdf.index < indexOfFirstAddedDocument) {
            return existingPdf;
          }

          return {
            ...existingPdf,
            index: existingPdf.index + newDocumentCount,
          };
        });
      }

      state.pdfData = updatedPdfData;
      state.pdfCount += newDocumentCount;
    },
    documentRemoved: (
      state,
      action: PayloadAction<{ readonly documentId: number; readonly pageIds: readonly number[] }>,
    ) => {
      const documentToDelete = state.pdfData[action.payload.documentId];

      const newDocuments = omitBy(state.pdfData, (document) => document.id === documentToDelete.id);
      const adjustedDocuments = mapValues(newDocuments, (document) => {
        if (document.index < documentToDelete.index) {
          return document;
        }

        return {
          ...document,
          index: document.index - 1,
        };
      });

      let documentIdToSelect = Object.keys(newDocuments).length === 0 ? null : state.activeDocumentId;
      if (action.payload.documentId === state.activeDocumentId) {
        const indexToSelect = Math.max(0, documentToDelete.index - 1);
        documentIdToSelect =
          Object.values(adjustedDocuments).find((document) => document.index === indexToSelect)?.id ?? null;
      }

      state.activeDocumentId = documentIdToSelect;
      state.activePageNumber = 0;
      state.pdfData = adjustedDocuments;
      state.pdfCount -= 1;
    },
    documentPageCreated: (
      state,
      action: PayloadAction<{ readonly documentId: number; readonly createdPage: PdfPage }>,
    ) => {
      const { documentId, createdPage } = action.payload;

      state.pdfData[documentId].pdfPages = state.pdfData[documentId].pdfPages
        .map((page) => {
          if (page.pageNumber >= createdPage.pageNumber) {
            return { ...page, pageNumber: page.pageNumber + 1 };
          }

          return page;
        })
        .concat([createdPage]);
    },
    documentPageDeleted: (state, action: PayloadAction<{ readonly documentId: number; readonly pageId: number }>) => {
      const { documentId, pageId } = action.payload;

      const pageToBeDeleted = state.pdfData[documentId].pdfPages.find((page) => page.id === pageId)!;

      // If the currently selected page was deleted, we have to select a new one
      let { activePageNumber, activeDocumentId } = state; // eslint-disable-line prefer-const
      if (activeDocumentId === documentId && activePageNumber === pageToBeDeleted.pageNumber) {
        // Keep active page index the same
        activePageNumber = pageToBeDeleted.pageNumber;

        // If was last page go to new last page
        if (activePageNumber >= state.pdfData[documentId].pdfPages.length - 1) {
          activePageNumber -= 1;
        }
      }

      state.activePageNumber = activePageNumber;
      state.pdfData[documentId].pdfPages = state.pdfData[documentId].pdfPages
        .filter((page) => page.id !== pageId)
        .map((page) => {
          if (page.pageNumber > pageToBeDeleted.pageNumber) {
            return { ...page, pageNumber: page.pageNumber - 1 };
          }

          return page;
        });
    },
    documentReplaced: (state, action: PayloadAction<{ readonly oldDocumentId: number; newDocument: Document }>) => {
      const { oldDocumentId, newDocument } = action.payload;
      const newPdfData = omitBy(state.pdfData, (document) => document.id === oldDocumentId);
      let { activeDocumentId } = state;
      if (activeDocumentId === oldDocumentId) activeDocumentId = newDocument.id;

      state.activeDocumentId = activeDocumentId;
      state.pdfData = {
        ...newPdfData,
        [newDocument.id]: newDocument,
      };
    },
    documentNotesModified: (
      state,
      action: PayloadAction<{ readonly documentId: number; readonly newNotes: string }>,
    ) => {
      const { documentId, newNotes } = action.payload;

      if (state.pdfData[documentId]) {
        state.pdfData[documentId].notes = newNotes;
        state.pdfData[documentId].notesModified = true;
      } else {
        // TODO: how to handle?
      }
    },
    pagesFlyoutOpened: (state, action: PayloadAction<{ readonly documentId: number }>) => {
      state.pagesFlyout = { isOpen: true, requestedDocumentId: action.payload.documentId };
    },
    pagesFlyoutClosed: (state) => {
      state.pagesFlyout = { isOpen: false };
    },
    documentNotesSaved: (state) => {
      state.pdfData = mapValues(state.pdfData, (document) => omit(document, ['notesModified']));
    },
    documentSelected: (state, action: PayloadAction<{ readonly documentId: number }>) => {
      state.activeDocumentId = action.payload.documentId;
      state.activePageNumber = 0;
      state.pagesFlyout = { isOpen: false };
    },
    documentPageSelected: (state, action: PayloadAction<{ readonly pageNumber: number }>) => {
      state.activePageNumber = action.payload.pageNumber;
      state.pagesFlyout = { isOpen: false };
    },
    documentIndexChangesApplied: (state, action: PayloadAction<{ readonly indexMap: IndexMap }>) => {
      const pdfDataAsArray = Object.values(state.pdfData);
      const orderedPdfsArray = pdfDataAsArray.map((pdf) => ({
        ...pdf,
        index: action.payload.indexMap[pdf.id],
      }));

      state.pdfData = keyBy(orderedPdfsArray, (pdf) => pdf.id);
    },
    designServiceComponentIdLoaded: (
      state,
      action: PayloadAction<{ readonly documentId: number; readonly designServiceComponentId: string | undefined }>,
    ) => {
      const { documentId, designServiceComponentId } = action.payload;

      state.pdfData[documentId].designServiceComponentId = designServiceComponentId;
    },
  },
});

export const {
  jobReset,
  documentsLoaded,
  documentRemoved,
  documentPageCreated,
  documentPageDeleted,
  documentReplaced,
  documentNotesModified,
  pagesFlyoutOpened,
  pagesFlyoutClosed,
  documentNotesSaved,
  documentSelected,
  documentPageSelected,
  documentIndexChangesApplied,
  designServiceComponentIdLoaded,
} = documentsSlice.actions;
export const documents = documentsSlice.reducer;
